import type {
  DayKey,
  RestaurantHours,
  RestaurantScheduling,
  SchedulingException,
} from '@/api/types'
import { hmToMinutes } from '@/shared/lib/time'

export const DAY_KEYS: Array<DayKey> = [
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
]

export const FULL_DAY_OPEN = '00:00'
export const FULL_DAY_CLOSE = '24:00'

export function isFullDay(day: {
  closed: boolean
  open?: string
  close?: string
}): boolean {
  return (
    !day.closed && day.open === FULL_DAY_OPEN && day.close === FULL_DAY_CLOSE
  )
}

function defaultWeek(open: string, close: string): RestaurantHours {
  const out = {} as RestaurantHours
  DAY_KEYS.forEach((d) => (out[d] = { closed: false, open, close }))
  return out
}

/** Mirrors the server-side defaults so a fresh page renders a valid config. */
export function defaultScheduling(): RestaurantScheduling {
  return {
    unmannedHours: defaultWeek('11:00', '21:00'),
    staffedHours: defaultWeek('11:00', '21:00'),
    ordering: { opensLocal: '08:00', closesLocal: null },
    slots: {
      slotDurationMinutes: 10,
      serviceStartLocal: '11:10',
      serviceEndLocal: '21:00',
    },
    leadWindows: [
      { untilLocal: '11:00', minAdvanceMinutes: 30 },
      { afterLocal: '11:00', minAdvanceMinutes: 60 },
    ],
    exceptions: [],
  }
}

export interface NestingWarnings {
  pickupOutsideStaffed: Array<DayKey>
  staffedOutsideUnmanned: Array<DayKey>
}

/**
 * Advisory (non-blocking) containment check: pickup window within staffed hours,
 * staffed hours within unmanned hours, per weekday. Closed staffed days are skipped
 * for the pickup check because unmanned pickup is a valid model.
 */
export function nestingWarnings(s: RestaurantScheduling): NestingWarnings {
  const pickupOutsideStaffed: Array<DayKey> = []
  const staffedOutsideUnmanned: Array<DayKey> = []
  const pickupStart = hmToMinutes(s.slots.serviceStartLocal)
  const pickupEnd = hmToMinutes(s.slots.serviceEndLocal)
  for (const d of DAY_KEYS) {
    const staffed = s.staffedHours[d]
    const unmanned = s.unmannedHours[d]
    if (!staffed.closed) {
      if (
        unmanned.closed ||
        hmToMinutes(unmanned.open ?? '00:00') >
          hmToMinutes(staffed.open ?? '00:00') ||
        hmToMinutes(unmanned.close ?? '00:00') <
          hmToMinutes(staffed.close ?? '00:00')
      ) {
        staffedOutsideUnmanned.push(d)
      }
      if (
        pickupStart < hmToMinutes(staffed.open ?? '00:00') ||
        pickupEnd > hmToMinutes(staffed.close ?? '00:00')
      ) {
        pickupOutsideStaffed.push(d)
      }
    }
  }
  return { pickupOutsideStaffed, staffedOutsideUnmanned }
}

/** Adds an exception for a date; returns null when the date is empty or already present. */
export function addException(
  exceptions: Array<SchedulingException>,
  date: string,
): Array<SchedulingException> | null {
  if (!date) return null
  if (exceptions.some((e) => e.date === date)) return null
  const next = [...exceptions, { date }]
  next.sort((a, b) => a.date.localeCompare(b.date))
  return next
}

export function removeException(
  exceptions: Array<SchedulingException>,
  date: string,
): Array<SchedulingException> {
  return exceptions.filter((e) => e.date !== date)
}

export function updateException(
  exceptions: Array<SchedulingException>,
  date: string,
  patch: Partial<SchedulingException>,
): Array<SchedulingException> {
  return exceptions.map((e) => (e.date === date ? { ...e, ...patch } : e))
}

/** Today's date (yyyy-MM-dd) in the device timezone, for hiding past exceptions. */
export function todayIso(): string {
  const d = new Date()
  const pad = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
